import type { CustomerDTO } from "@ifa/utils";
import { useBrandAssets } from "@ifa/ui";

import { ZuuFinancialSection } from "./ZuuFinancialSection";
import { ZuuFnaSection } from "./ZuuFnaSection";
import { ZuuRiskAssessmentSection } from "./ZuuRiskAssessmentSection";
import styles from "./ZuuCustomerDetailView.module.css";

type ZuuAnchorSection = {
  id: string;
  step: string;
  title: string;
  description: string;
  meta: string;
};

export function ZuuCustomerDetailView({ customer }: { customer: CustomerDTO | null }) {
  const brandAssets = useBrandAssets();
  const anchorSections: ZuuAnchorSection[] = [
    {
      id: "zuu-financial",
      step: "01",
      title: "財務資料",
      description: "收入、支出、資產、負債與現有保障。",
      meta: customer?.financial ? "已接入 financial" : "待補 financial",
    },
    {
      id: "zuu-fna",
      step: "02",
      title: "FNA",
      description: "需求分析、分析答案與產品偏好。",
      meta: `${customer?.financial?.financialAnalysis?.length ?? 0} 條分析答案`,
    },
    {
      id: "zuu-risk",
      step: "03",
      title: "風險評估",
      description: "CRA 問卷查看與風險分組結果。",
      meta: customer?.sourceOrderId ? `來源訂單 #${customer.sourceOrderId}` : "無來源訂單",
    },
  ];

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.heroMain}>
          {brandAssets.logo ? (
            <div className={styles.brandPill}>
              <img src={brandAssets.logo} alt="ZUU" className={styles.brandLogo} />
            </div>
          ) : null}
          <div>
            <p className={styles.eyebrow}>ZUU viewing mode</p>
            <h2 className={styles.title}>ZUU 客戶資料查看頁</h2>
            <p className={styles.description}>
              將原本的 dashboard 式摘要改成長表單閱讀結構，方便依序核對客戶財務資料、FNA 與風險評估。
            </p>
          </div>
        </div>
        <div className={styles.headerMetaList}>
          <span className={styles.headerMeta}>原路由不變</span>
          <span className={styles.headerMeta}>只替換展示層</span>
          <span className={styles.headerMeta}>保留原資料語義</span>
        </div>
      </header>

      <div className={styles.layout}>
        <aside className={styles.sideRail}>
          <div className={styles.sideRailCard}>
            <div className={styles.sideRailTitle}>頁內導引</div>
            <div className={styles.sideRailSubtitle}>依長表單順序查看 ZUU 專屬內容。</div>
            <nav className={styles.anchorList} aria-label="ZUU sections">
              {anchorSections.map((section) => (
                <a key={section.id} href={`#${section.id}`} className={styles.anchorLink}>
                  <span className={styles.anchorStep}>{section.step}</span>
                  <span className={styles.anchorBody}>
                    <span className={styles.anchorTitle}>{section.title}</span>
                    <span className={styles.anchorDesc}>{section.description}</span>
                    <span className={styles.anchorMeta}>{section.meta}</span>
                  </span>
                </a>
              ))}
            </nav>
          </div>
        </aside>

        <div className={styles.content}>
          <section id="zuu-financial" className={styles.sectionBlock}>
            <div className={styles.sectionLead}>
              <span className={styles.sectionLeadStep}>01</span>
              <div>
                <h3 className={styles.sectionLeadTitle}>財務資料</h3>
                <p className={styles.sectionLeadDesc}>以表單查看態連續展示收入、支出、資產與現有保障。</p>
              </div>
            </div>
            <ZuuFinancialSection customer={customer} />
          </section>

          <section id="zuu-fna" className={styles.sectionBlock}>
            <div className={styles.sectionLead}>
              <span className={styles.sectionLeadStep}>02</span>
              <div>
                <h3 className={styles.sectionLeadTitle}>FNA</h3>
                <p className={styles.sectionLeadDesc}>保留原資料來源，改用連續閱讀頁的節奏承接需求分析內容。</p>
              </div>
            </div>
            <ZuuFnaSection data={customer} />
          </section>

          <section id="zuu-risk" className={styles.sectionBlock}>
            <div className={styles.sectionLead}>
              <span className={styles.sectionLeadStep}>03</span>
              <div>
                <h3 className={styles.sectionLeadTitle}>風險評估</h3>
                <p className={styles.sectionLeadDesc}>沿頁內順序繼續查看 CRA 問卷、答案映射與來源訂單結果。</p>
              </div>
            </div>
            <ZuuRiskAssessmentSection customer={customer} />
          </section>
        </div>
      </div>
    </div>
  );
}
